import React, { useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import WidgetModel from '@mm/state/model/WidgetModel';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import './TextButtonsList.pcss';
import {
  SortableContainer,
  SortableElement,
} from '../../../lib/react-sortable-hoc';
import { TextButton } from '../TextButton/TextButton';
import { useButtonHandlers } from '../../../helpers/widget-helpers/use-button-handlers';

type Props = {
  chatbotId: number;
  widget: WidgetModel;
  forAds?: boolean;
  forInstagram?: boolean;
  disabledButtonTypes?: ButtonType[];
  openedButtonId: number | undefined;
  setOpenedButtonId: (openedButtonId: number | undefined) => void;
  usedInSlideout?: boolean;
};

const DnDButtonComponent = SortableElement(TextButton);

const ButtonsContainer = SortableContainer(
  ({ children, ...props }: { [key: string]: any }) => (
    <div {...props}>{children}</div>
  ),
);

export const TextButtonsList = (props: Props) => {
  const {
    widget,
    forAds,
    forInstagram,
    disabledButtonTypes,
    openedButtonId,
    setOpenedButtonId,
    chatbotId,
    usedInSlideout,
  } = props;
  const [selectedButton, setSelectedButton] = useState<number | undefined>();
  const buttonHandlers = useButtonHandlers();
  const buttons = useMemo(
    (): ButtonModel[] => sortBy(widget.buttons, ['sortOrder']),
    [widget.buttons],
  );

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) return;

    const newButtons = [...buttons];

    const { sortOrder: newSortOrder } = newButtons[newIndex];
    const targetButton = { ...newButtons[oldIndex] };

    buttonHandlers.updateButton(widget, {
      ...targetButton,
      sortOrder: newSortOrder,
    });

    if (oldIndex < newIndex) {
      for (let i = oldIndex + 1; i <= newIndex; i += 1) {
        const button = newButtons[i];

        buttonHandlers.updateButton(widget, {
          ...button,
          sortOrder: button.sortOrder - 1,
        });
      }
    }

    if (oldIndex > newIndex) {
      for (let i = newIndex; i < oldIndex; i += 1) {
        const button = newButtons[i];

        buttonHandlers.updateButton(widget, {
          ...button,
          sortOrder: button.sortOrder + 1,
        });
      }
    }
  };

  return (
    <ButtonsContainer
      className="text-buttons-list"
      distance={15}
      lockAxis="y"
      lockOffset={0}
      lockToContainerEdges
      onSortEnd={onSortEnd}
    >
      {buttons.map((button, index: number) => (
        <DnDButtonComponent
          index={index}
          dropdownOpen={selectedButton === button.id}
          setSelectedButton={setSelectedButton}
          button={button}
          widget={widget}
          key={button.id}
          tooltip={
            disabledButtonTypes?.includes(button.type) && forInstagram
              ? `This type of button is not supported on Instagram. 
                Use a Quick Question instead`
              : ''
          }
          disabledButtonTypes={disabledButtonTypes}
          forAds={forAds}
          openedButtonId={openedButtonId}
          setOpenedButtonId={setOpenedButtonId}
          chatbotId={chatbotId}
          usedInSlideout={usedInSlideout}
        />
      ))}
    </ButtonsContainer>
  );
};
